import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { toastSuccess, toastError } from '../../../lib/toast';
import ProfileCard from '../ui/ProfileCard';
import CardState from '../ui/CardState';

const PREF_LABELS = [
  ['email_pref_app_submissions', 'New app submissions'],
  ['email_pref_tool_requests', 'New tool requests & proposals'],
  ['email_pref_daily_digest', 'Daily admin digest'],
];

export default function EmailOptionsTab({ data }) {
  const [prefs, setPrefs] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  // One save at a time: a second tick racing the first would be overwritten
  // by the first one's rollback if that save failed.
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data?.is_admin) return undefined;
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/api/profile/email-prefs`, { withCredentials: true });
        if (active) setPrefs(r.data);
      } catch {
        if (active) setUnavailable(true);
      }
    })();
    return () => { active = false; };
  }, [data?.is_admin]);

  const toggle = async (key) => {
    if (saving) return;
    const snapshot = prefs;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/profile/email-prefs`, next, { withCredentials: true });
      toastSuccess('Email preferences updated.');
    } catch {
      setPrefs(snapshot);
      toastError("Couldn't update email preferences.");
    } finally {
      setSaving(false);
    }
  };

  // A non-admin has no preferences to set -- the endpoints are admin-only --
  // but they still get the line explaining what email they receive. Dropping
  // it would leave them with no answer to "what does Nexus email me about?".
  if (!data?.is_admin) {
    return (
      <ProfileCard title="Email options">
        <p className="profile-muted profile-info-card__empty">
          App status updates are always sent to your inbox.
        </p>
      </ProfileCard>
    );
  }

  return (
    <ProfileCard title="Email options">
      {unavailable && <CardState kind="error" thing="email preferences" />}
      {!unavailable && !prefs && <CardState kind="loading" label="Loading email options" lines={3} />}
      {prefs && (
        <>
        {/* This card sits under the details form's Save button but does not
            use it: each tick is saved on its own. */}
        <p className="pers-hint">Saved as soon as you tick a box.</p>
        <ul className="profile-prefs" aria-busy={saving}>
          {PREF_LABELS.map(([key, label]) => (
            <li key={key} className="profile-pref-row">
              <label>
                <input type="checkbox" checked={!!prefs[key]} disabled={saving} onChange={() => toggle(key)} />
                <span>{label}</span>
              </label>
            </li>
          ))}
        </ul>
        </>
      )}
    </ProfileCard>
  );
}
